import React from "react";
import { render, screen, waitFor } from "@testing-library/react";
import "@testing-library/jest-dom";

// pdvEnabled must read workspace.enabled_integrations, fail closed when
// absent/empty/not-containing "pdv". See CompanyProfile.tsx:158.
const mockUseWorkspaceContext = jest.fn();
jest.mock("../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => mockUseWorkspaceContext(),
}));

// Default lives in the factory: CRA's resetMocks wipes mock implementations.
const mockParams = jest.fn();
jest.mock("react-router-dom", () => ({
  useParams: () => mockParams() ?? { company_id: "company-1" },
  useNavigate: () => jest.fn(),
  useLocation: () => ({ pathname: "/", search: "", state: null }),
}));

const mockBKGRead = jest.fn().mockResolvedValue({
  _id: "workspace-1/company/company-1",
  value: { name: { value: "Acme Biotech" } },
});
const mockBKGCompanyInvestors = jest.fn().mockResolvedValue({ items: [] });
jest.mock("../hooks", () => ({
  useBKGRead: () => mockBKGRead,
  useBKGCompanyInvestors: () => mockBKGCompanyInvestors,
}));

jest.mock("../utils/profileBackNavigation", () => ({
  COMPANY_PROFILE_BACK_SOURCE: {
    catalystEvents: "catalyst-events",
    vcProfile: "vc-profile",
  },
  VC_PROFILE_BACK_SOURCE: { companyProfile: "company-profile" },
  buildVCHref: () => "/",
  resolveRouteDocumentId: jest.requireActual("../utils/profileBackNavigation")
    .resolveRouteDocumentId,
  useProfileBackTag: () => ({ from: undefined, inFreshTabContext: false }),
}));

jest.mock("../../../components/Tutorial", () => ({
  useTutorialTarget: () => ({ current: null }),
  useTutorialHint: () => {},
}));

jest.mock("../components/BKGAgent", () => ({
  FloatingChatWidget: () => <div data-testid="floating-chat-widget" />,
}));

jest.mock("../components/CompanyCatalystEvents", () => () => (
  <div data-testid="company-catalyst-events" />
));

jest.mock("../components/PDVCompanyConnections", () => () => (
  <div data-testid="pdv-company-connections" />
));

const CompanyProfile = require("./CompanyProfile").default;

const buildWorkspace = (overrides: Record<string, unknown> = {}) => ({
  id: "workspace-1",
  name: "Workspace",
  ...overrides,
});

describe("CompanyProfile pdvEnabled gate", () => {
  beforeEach(() => {
    jest.clearAllMocks();
    mockBKGRead.mockResolvedValue({
      _id: "workspace-1/company/company-1",
      value: { name: { value: "Acme Biotech" } },
    });
    mockBKGCompanyInvestors.mockResolvedValue({ items: [] });
  });

  it('renders the PDV panel when enabled_integrations includes "pdv"', async () => {
    mockUseWorkspaceContext.mockReturnValue({
      workspace: buildWorkspace({ enabled_integrations: ["pdv"] }),
    });

    render(<CompanyProfile />);

    expect(
      await screen.findByTestId("pdv-company-connections"),
    ).toBeInTheDocument();
  });

  it("hides the PDV panel when enabled_integrations is absent", async () => {
    mockUseWorkspaceContext.mockReturnValue({
      workspace: buildWorkspace(),
    });

    render(<CompanyProfile />);

    await waitFor(() => expect(mockBKGRead).toHaveBeenCalled());
    expect(
      screen.queryByTestId("pdv-company-connections"),
    ).not.toBeInTheDocument();
  });

  it("hides the PDV panel when enabled_integrations is empty", async () => {
    mockUseWorkspaceContext.mockReturnValue({
      workspace: buildWorkspace({ enabled_integrations: [] }),
    });

    render(<CompanyProfile />);

    await waitFor(() => expect(mockBKGRead).toHaveBeenCalled());
    expect(
      screen.queryByTestId("pdv-company-connections"),
    ).not.toBeInTheDocument();
  });

  it('hides the PDV panel when enabled_integrations does not include "pdv"', async () => {
    mockUseWorkspaceContext.mockReturnValue({
      workspace: buildWorkspace({ enabled_integrations: ["hubspot"] }),
    });

    render(<CompanyProfile />);

    await waitFor(() => expect(mockBKGRead).toHaveBeenCalled());
    expect(
      screen.queryByTestId("pdv-company-connections"),
    ).not.toBeInTheDocument();
  });
});

// New tabs and copied links carry only the URL (no navigation state), so the
// route id alone must resolve to the document the in-app click fetched.
describe("CompanyProfile route id resolution", () => {
  beforeEach(() => {
    jest.clearAllMocks();
    mockUseWorkspaceContext.mockReturnValue({ workspace: buildWorkspace() });
  });

  it.each([
    ["a slug", "company-1", "workspace-1/company/company-1"],
    [
      "a UUID",
      "3f2b8c1e-9a4d-4e6f-8b2a-1c5d7e9f0a3b",
      "3f2b8c1e-9a4d-4e6f-8b2a-1c5d7e9f0a3b",
    ],
    [
      "a legacy ObjectId",
      "64b7f0c2a1b2c3d4e5f60718",
      "64b7f0c2a1b2c3d4e5f60718",
    ],
    [
      "a slash-containing id",
      "workspace-1/company/astrazeneca/daiichi_sankyo",
      "workspace-1/company/astrazeneca/daiichi_sankyo",
    ],
  ])("fetches %s as its own document id", async (_label, routeId, expected) => {
    mockParams.mockImplementation(() => ({ company_id: routeId }));

    render(<CompanyProfile />);

    await waitFor(() =>
      expect(mockBKGRead).toHaveBeenCalledWith(
        "workspace-1",
        "company",
        expected,
      ),
    );
  });
});
